// In-editor combat playtest: a minimal, Studio-native renderer + HUD driven
// entirely by `@sstk/sunder-sim`'s own `BreachPointEngine` - no simulation
// logic lives here, only geometry drawing and input plumbing. See
// docs/in-editor-combat-playtest-plan.md for the scoping rationale (why this
// is a lightweight renderer rather than embedding the real sstk-sunder-web
// client, and why bots/collision/rules are 100% reused, never reimplemented).
import { Canvas, useFrame } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import {
  BreachPointEngine,
  PLAYER_CROUCH_EYE_HEIGHT,
  PLAYER_EYE_HEIGHT,
  type AgentSnapshot,
  type BreachPointSnapshot,
  type Wall,
} from '@sstk/sunder-sim';
import type { MapDocument } from '../../engine';
import { resolvePlaytestGameMode } from './playtestValidation';
import { PlaytestInputController } from './playtestInput';
import { PlaytestHud } from './PlaytestHud';

export interface PlaytestOverlayProps {
  /** The live editor document, already validated by the caller. */
  mapDocument: MapDocument;
  /** Return to editing. The editor underneath was never unmounted. */
  onExit: () => void;
}

const TEAM_COLOR: Record<string, string> = {
  raiders: '#e2703a',
  wardens: '#3aa0e2',
};

function WallMesh({ wall }: { wall: Wall }) {
  return (
    <mesh position={[wall.cx, wall.cy, wall.cz]}>
      <boxGeometry args={[wall.hw * 2, wall.hh * 2, wall.hd * 2]} />
      <meshStandardMaterial color={wall.isCover ? '#7a6a55' : '#5c6470'} />
    </mesh>
  );
}

function AgentMesh({ agent }: { agent: AgentSnapshot }) {
  const height = agent.crouching ? 1.1 : 1.8;
  return (
    <group position={[agent.x, agent.y, agent.z]} rotation={[0, -agent.yaw, 0]} visible={agent.alive}>
      <mesh position={[0, height / 2, 0]}>
        <capsuleGeometry args={[0.32, Math.max(0.1, height - 0.64), 4, 8]} />
        <meshStandardMaterial color={TEAM_COLOR[agent.team] ?? '#aaaaaa'} />
      </mesh>
      {/* A short nose so facing is readable at a glance. */}
      <mesh position={[0, height * 0.8, 0.32]}>
        <boxGeometry args={[0.12, 0.12, 0.3]} />
        <meshStandardMaterial color="#111318" />
      </mesh>
    </group>
  );
}

/** Drives the engine loop and the first-person camera from inside <Canvas>. */
function PlaytestScene({
  engine,
  onSnapshot,
}: {
  engine: BreachPointEngine;
  onSnapshot: (snapshot: BreachPointSnapshot) => void;
}) {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const [liveAgents, setLiveAgents] = useState<AgentSnapshot[]>([]);

  useFrame((_state, delta) => {
    // Auto-confirm the buy phase every frame: confirmBuy() is a no-op unless
    // phase is exactly 'buy', so this just skips the buy screen the instant
    // it appears rather than needing a buy-menu UI (see the plan doc's
    // "known limitations" - no economy in this first version).
    engine.confirmBuy();
    // Clamp to 100ms so a stalled/backgrounded tab does not fast-forward the
    // sim through a huge single step when it resumes.
    engine.update(Math.min(delta, 0.1) * 1000);
    const snapshot = engine.snapshot();
    onSnapshot(snapshot);
    setLiveAgents(snapshot.agents.filter((a) => !a.isPlayer));

    const camera = cameraRef.current;
    if (!camera) return;
    const eyeHeight = snapshot.playerCrouching ? PLAYER_CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT;
    camera.position.set(snapshot.playerX, snapshot.playerY + eyeHeight, snapshot.playerZ);
    // Same convention as sstk-sunder-web's own render/first-person-cam.ts:
    // YXZ order, Three.js yaw is the negation of the engine's (opposite
    // rotation sense), pitch maps straight across.
    camera.rotation.order = 'YXZ';
    camera.rotation.y = -snapshot.playerYaw;
    camera.rotation.x = snapshot.playerPitch;
    camera.rotation.z = 0;
  });

  return (
    <>
      <PerspectiveCamera ref={cameraRef} makeDefault fov={90} near={0.05} far={200} />
      <ambientLight intensity={0.65} />
      <directionalLight position={[20, 30, 10]} intensity={0.9} />
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[200, 200]} />
        <meshStandardMaterial color="#2a2d33" />
      </mesh>
      {engine.map.walls.map((wall, i) => (
        // Wall documents carry no stable id; index is fine here, this list
        // only changes when a new playtest session (a fresh engine) mounts.
        <WallMesh key={i} wall={wall} />
      ))}
      {liveAgents.map((agent) => (
        <AgentMesh key={agent.id} agent={agent} />
      ))}
    </>
  );
}

export function PlaytestOverlay({ mapDocument, onExit }: PlaytestOverlayProps) {
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const [snapshot, setSnapshot] = useState<BreachPointSnapshot | null>(null);
  const [locked, setLocked] = useState(false);
  const [inputReady, setInputReady] = useState(false);

  // One engine for the life of this overlay. loadMap()/start() never touch
  // module-level active-map state, so this never disturbs the editor
  // underneath or any other concurrent use of the sim.
  const engine = useMemo(() => {
    const e = new BreachPointEngine();
    e.loadMap(mapDocument);
    e.start('raiders', 1, resolvePlaytestGameMode(mapDocument), 'normal');
    return e;
    // Intentionally built once per mount: this overlay is remounted (via its
    // key) whenever the creator launches a fresh playtest, so there is
    // never a stale engine to react to a document change here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputRef = useRef<PlaytestInputController | null>(null);

  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host) return;
    const controller = new PlaytestInputController(host);
    engine.setPlayerInput(controller.state);
    inputRef.current = controller;
    setInputReady(true);

    const onLockChange = () => setLocked(document.pointerLockElement === host);
    document.addEventListener('pointerlockchange', onLockChange);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') onExit();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerlockchange', onLockChange);
      window.removeEventListener('keydown', onKeyDown);
      controller.dispose();
      inputRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div ref={canvasHostRef} style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {inputReady && (
          <Canvas>
            <PlaytestScene engine={engine} onSnapshot={setSnapshot} />
          </Canvas>
        )}
        {!locked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(6,6,8,0.55)',
              color: '#fff',
              fontFamily: 'var(--ss-sans, sans-serif)',
              cursor: 'pointer',
              textAlign: 'center',
            }}
            onClick={() => inputRef.current?.requestLock()}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Click to play</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                WASD to move, mouse to look, click to fire, R to reload, 1/2/3 to switch weapon, Esc to exit
              </div>
            </div>
          </div>
        )}
        <PlaytestHud snapshot={snapshot} onExit={onExit} />
      </div>
    </div>
  );
}
